"use client"

import { notify } from "@/lib/notifications/notify"
import { useState, useEffect } from "react"
import { formatCity, formatState } from "@/lib/format-location"
import {
  Bell,
  X,
  DollarSign,
  CheckCircle,
  XCircle,
  MessageSquare,
  Briefcase,
  MapPin,
  CheckCheck,
  AlertCircle,
  Calendar,
} from "lucide-react"
import { createBrowserClient } from "@/lib/supabase/client"
import { SwipeToDelete } from "@/components/swipe-to-delete"
import type { RealtimePostgresInsertPayload } from "@supabase/supabase-js"
import Link from "next/link"
import { useNotificationSheet, NotificationSheetGrip } from "@/components/notifications-sheet"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import confetti from "canvas-confetti"
import { Lozenge } from "@/components/ui/lozenge"

type Notification = {
  id: string
  type: string
  title: string
  message: string
  metadata: {
    amount?: number
    shopName?: string
    shop_name?: string
    shop_id?: string
    owner_id?: string
    invite_token?: string
    conversation_id?: string
    /** Written by job-inquire-modal: who asked. Used to open the thread with
     *  them rather than dropping the user in the inbox. */
    barber_id?: string
    reservation_id?: string
    start_date?: string
  }
  read: boolean
  created_at: string
}

type ShopDetails = {
  id: string
  name: string
  address: string
  city: string
  state: string
  cover_image: string | null
  booth_plans: {
    id: string
    plan_type: string
    price: number
  }[]
}

export function BarberNotificationsPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const sheet = useNotificationSheet(isOpen)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [expandedNotification, setExpandedNotification] = useState<Notification | null>(null)
  const [workRequestNotification, setWorkRequestNotification] = useState<Notification | null>(null)
  const [workRequestShop, setWorkRequestShop] = useState<ShopDetails | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)
  const [newShopBarberId, setNewShopBarberId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all")
  const [userId, setUserId] = useState<string | null>(null)
  const router = useRouter()

  // Remove one notification. RLS has allowed this since 016
  // ("Users can delete their own notifications", auth.uid() = user_id) — it
  // simply had no way to be triggered outside the full notifications page.
  // Optimistic: the row is already sliding out, so waiting on the round trip
  // would show it snap back for a frame. On failure it is put back.
  async function deleteNotification(id: string) {
    const previous = notifications
    setNotifications((prev) => prev.filter((n) => n.id !== id))
    const { error } = await createBrowserClient().from("notifications").delete().eq("id", id)
    if (error) {
      console.error("[notifications] delete failed:", error.message)
      setNotifications(previous)
    }
  }

  const unreadCount = notifications.filter((n) => !n.read).length

  // Close panel on escape key and handle body scroll
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [isOpen])

  useEffect(() => {
    fetchNotifications()

    const supabase = createBrowserClient()

    async function setupSubscription() {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) return
      setUserId(user.id)

      const channel = supabase
        .channel("barber_notifications_realtime")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`,
          },
          (payload: RealtimePostgresInsertPayload<Notification>) => {
            setNotifications((prev) => [payload.new as Notification, ...prev])
          },
        )
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }

    const cleanup = setupSubscription()

    return () => {
      cleanup.then((fn) => fn && fn())
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen])

  async function fetchNotifications() {
    setIsLoading(true)
    const supabase = createBrowserClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      setIsLoading(false)
      return
    }

    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .neq("type", "profile_view")
      .order("created_at", { ascending: false })
      .limit(50)

    if (data) {
      setNotifications(data)
    }
    setIsLoading(false)
  }

  async function markAsRead(id: string) {
    const supabase = createBrowserClient()

    const { error } = await supabase
      .from("notifications")
      .update({ read: true, updated_at: new Date().toISOString() })
      .eq("id", id)

    if (!error) {
      setNotifications(notifications.map((n) => (n.id === id ? { ...n, read: true } : n)))
    }
  }

  async function markAllAsRead() {
    if (!userId) return
    const supabase = createBrowserClient()

    const { error } = await supabase
      .from("notifications")
      .update({ read: true, updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("read", false)

    if (!error) {
      setNotifications(notifications.map((n) => ({ ...n, read: true })))
    }
  }

  async function fetchShopDetails(shopId: string) {
    const supabase = createBrowserClient()
    const { data } = await supabase
      .from("shops")
      .select(`
        id,
        name,
        address,
        city,
        state,
        cover_image,
        booth_plans (
          id,
          plan_type,
          price
        )
      `)
      .eq("id", shopId)
      .single()

    if (data) {
      setWorkRequestShop(data as ShopDetails)
    }
  }

  async function handleAcceptWorkRequest() {
    if (!workRequestNotification?.metadata?.shop_id || !workRequestNotification?.metadata?.invite_token) return

    setIsAccepting(true)
    const supabase = createBrowserClient()

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error("Not authenticated")

      const { error: inviteError } = await supabase
        .from("barber_invitations")
        .update({
          status: "accepted",
          used_at: new Date().toISOString(),
        })
        .eq("invite_token", workRequestNotification.metadata.invite_token)

      if (inviteError) throw inviteError

      // Accepting a work request is accepting an invitation — same consent,
      // same result: on the floor now, rent set up after. This used to land
      // 'pending_payment', which the owner's roster did not even show.
      const { data: shopBarber, error: shopBarberError } = await supabase
        .from("shop_barbers")
        .insert({
          shop_id: workRequestNotification.metadata.shop_id,
          barber_id: user.id,
          status: "active",
          joined_at: new Date().toISOString(),
        })
        .select()
        .single()

      // 23505 = already on this roster, which is the end state we wanted.
      if (shopBarberError && shopBarberError.code !== "23505") throw shopBarberError

      if (workRequestNotification.metadata.owner_id) {
        const { data: profile } = await supabase.from("profiles").select("full_name").eq("id", user.id).single()

        await notify({
          recipient: workRequestNotification.metadata.owner_id,
          type: "invitation_accepted",
          shopId: workRequestNotification.metadata.shop_id,
          metadata: {
            shop_id: workRequestNotification.metadata.shop_id,
            shop_name: workRequestNotification.metadata.shop_name,
            barber_id: user.id,
            barber_name: profile?.full_name,
          },
        })
      }

      markAsRead(workRequestNotification.id)

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#10b981", "#34d399", "#6ee7b7"],
      })

      setNewShopBarberId(shopBarber.id)
      // Navigate to the calendar booking flow
      const shopId = workRequestNotification.metadata.shop_id
      setWorkRequestNotification(null)
      setWorkRequestShop(null)
      setIsOpen(false)
      router.push(`/listings/${shopId}/book`)
    } catch (error) {
      console.error("Failed to accept work request:", error)
      alert("Failed to accept invitation. Please try again.")
    } finally {
      setIsAccepting(false)
    }
  }

  async function handleDeclineWorkRequest() {
    if (!workRequestNotification?.metadata?.invite_token) return

    setIsDeclining(true)
    const supabase = createBrowserClient()

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error("Not authenticated")

      const { error: inviteError } = await supabase
        .from("barber_invitations")
        .update({
          status: "declined",
          used_at: new Date().toISOString(),
        })
        .eq("invite_token", workRequestNotification.metadata.invite_token)

      if (inviteError) throw inviteError

      if (workRequestNotification.metadata.owner_id) {
        const { data: profile } = await supabase.from("profiles").select("full_name").eq("id", user.id).single()

        await notify({
          recipient: workRequestNotification.metadata.owner_id,
          type: "invitation_declined",
          shopId: workRequestNotification.metadata.shop_id,
          metadata: {
            shop_id: workRequestNotification.metadata.shop_id,
            shop_name: workRequestNotification.metadata.shop_name,
            barber_id: user.id,
          },
        })
      }

      markAsRead(workRequestNotification.id)
      setWorkRequestNotification(null)
      setWorkRequestShop(null)
    } catch (error) {
      console.error("Failed to decline work request:", error)
      alert("Failed to decline invitation. Please try again.")
    } finally {
      setIsDeclining(false)
    }
  }

  function handleNotificationClick(notification: Notification) {
    markAsRead(notification.id)

    if (notification.type === "work_request") {
      setWorkRequestNotification(notification)
      if (notification.metadata?.shop_id) {
        fetchShopDetails(notification.metadata.shop_id)
      }
      return
    }

    if (notification.type === "reservation_approved") {
      setExpandedNotification(notification)
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ["#10b981", "#34d399", "#6ee7b7"],
        })
      }, 200)
      setTimeout(() => {
        window.location.reload()
      }, 2000)
      return
    }

    if (notification.type === "reservation_rejected") {
      setExpandedNotification(notification)
      return
    }

    // Rental agreement → the dashboard home, where the Rental agreement card
    // (Review & Sign) now lives. The old per-shop page has been sunset.
    if (notification.type === "contract_sent") {
      setIsOpen(false)
      router.push("/dashboard/barber")
      return
    }

    // Every remaining type gets somewhere to land. Before this, the common
    // ones fell through to the metadata checks and did nothing at all:
    // session_booking carries only an appointment_id, and payment_received /
    // profile_view carry no conversation or shop — so tapping "X booked an
    // appointment" left you staring at the same panel.
    const destination = (() => {
      const m = notification.metadata || {}
      if (m.conversation_id) return `/dashboard/messages?conversation=${m.conversation_id}`
      switch (notification.type) {
        case "session_booking":
        case "appointment_booked":
        case "appointment_cancelled":
        case "appointment_rescheduled":
          return "/dashboard/barber/bookings"
        case "payment_received":
        case "payment_failed":
        case "payment_request":
        case "payment_reminder":
          return "/dashboard/barber"
        case "profile_view":
          return "/dashboard/barber"
        case "plan_offer":
          return m.shop_id ? `/listings/${m.shop_id}` : "/dashboard/barber#booths"
        case "job_inquiry":
          // Same as the owner panel: the inquiry is a message, not a job post.
          // A barber normally SENDS these rather than receives one, but a user
          // in barber mode can still be the shop owner being asked.
          return m.barber_id ? `/dashboard/messages?userId=${m.barber_id}` : "/dashboard/messages"
        default:
          return m.shop_id ? `/listings/${m.shop_id}` : null
      }
    })()

    if (destination) {
      setIsOpen(false)
      router.push(destination)
    }
  }

  function handleMessageOwner() {
    setExpandedNotification(null)
    setIsOpen(false)
    router.push("/dashboard/messages")
  }

  function handleBrowseShops() {
    setExpandedNotification(null)
    setIsOpen(false)
    router.push("/listings")
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "payment_reminder":
      case "payment_received":
        return <DollarSign className="h-4 w-4" />
      case "reservation_approved":
      case "invitation_accepted":
        return <CheckCircle className="h-4 w-4" />
      case "reservation_rejected":
      case "invitation_declined":
        return <XCircle className="h-4 w-4" />
      case "new_message":
        return <MessageSquare className="h-4 w-4" />
      case "work_request":
        return <Briefcase className="h-4 w-4" />
      case "session_booking":
      case "renewal_reminder":
        return <Calendar className="h-4 w-4" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    const diffInHours = Math.floor(diffInMinutes / 60)
    const diffInDays = Math.floor(diffInHours / 24)

    if (diffInMinutes < 1) return "Just now"
    if (diffInMinutes < 60) return `${diffInMinutes}m`
    if (diffInHours < 24) return `${diffInHours}h`
    if (diffInDays < 7) return `${diffInDays}d`
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }

  const filteredNotifications = activeTab === "unread"
    ? notifications.filter(n => !n.read)
    : notifications

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce((groups, notification) => {
    const date = new Date(notification.created_at)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    let key: string
    if (date.toDateString() === today.toDateString()) {
      key = "Today"
    } else if (date.toDateString() === yesterday.toDateString()) {
      key = "Yesterday"
    } else {
      key = date.toLocaleDateString("en-US", { month: "long", day: "numeric" })
    }

    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(notification)
    return groups
  }, {} as Record<string, Notification[]>)

  return (
    <>
      {/* Bell Icon Button — softer active scale + smoother transition,
          mirrors notifications-panel.tsx for cross-role consistency. */}
      <button
        onClick={() => setIsOpen(true)}
        className="relative rounded-full p-2 text-foreground hover:bg-muted/80 active:scale-95 transition-all duration-200"
        aria-label="Open notifications"
      >
        <Bell className="h-5 w-5" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[10px] font-bold tabular-nums leading-none tracking-tight flex items-center justify-center px-1 ring-2 ring-background">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Backdrop — subtle dim on both breakpoints so the panel reads
          as a foreground layer. Mobile gets a lighter overlay than
          desktop since the panel covers more viewport on mobile. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] transition-opacity duration-500 ease-out bg-black/10 sm:bg-black/20 backdrop-blur-sm"
          // The launcher fades while the sheet is open (FloatingNav watches
          // this attribute) — it was sitting under the sheet and grabbing taps.
          data-floating-nav="hide"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Notifications panel — see notifications-panel.tsx for the
          rationale (peek sheet + FloatingNav clearance below). Softer
          450ms fade+slide so it eases in rather than snapping. */}
      <div
        className={`notif-sheet fixed bg-background z-[60] flex flex-col overflow-hidden transform transition-[transform,opacity] duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform
          top-0 left-0 right-0
          sm:left-auto sm:right-0 sm:h-full sm:w-[400px] sm:shadow-2xl
          ${isOpen
            ? "translate-x-0 translate-y-0 opacity-100"
            : "-translate-y-full sm:translate-y-0 sm:translate-x-full opacity-0"}
        `}
        // Phone: height comes from the sheet hook (grip-resizable); sm+ keeps
        // the full-height tray. Radius lives in .notif-sheet (globals.css).
        style={{ height: sheet.height }}
      >
        {/* Panel Header */}
        <div className="flex-shrink-0 bg-background z-10 border-b border-border">
          <div className="notif-sheet-header flex items-center justify-between px-5 pb-4">
            <div>
              <h2 className="text-[22px] font-bold tracking-tight text-foreground">Notifications</h2>
              {unreadCount > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {unreadCount} unread
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-medium rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Close notifications"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Tabs — project segmented-pill (matches /listings density
              toggle + /community view tabs). Cleaner than the dated
              tab-with-underline approach. */}
          <div className="px-5 pb-3">
            <div className="inline-flex items-center gap-1 p-0.5 rounded-full bg-muted">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-3.5 py-1 text-xs font-medium rounded-full transition-all ${
                  activeTab === "all"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab("unread")}
                className={`px-3.5 py-1 text-xs font-medium rounded-full transition-all inline-flex items-center gap-1.5 ${
                  activeTab === "unread"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] font-bold tabular-nums leading-none tracking-tight flex items-center justify-center px-1">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Panel Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Bell className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="text-base font-medium text-foreground mb-1">
                {activeTab === "unread" ? "All caught up" : "No notifications yet"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-[260px]">
                {activeTab === "unread"
                  ? "You've read all your notifications"
                  : "When you receive notifications, they'll appear here"}
              </p>
            </div>
          ) : (
            <div>
              {/* Grouped Notifications */}
              {Object.entries(groupedNotifications).map(([date, dateNotifications]) => (
                <div key={date}>
                  <div className="sticky top-0 px-5 py-2 bg-muted/30 backdrop-blur-sm">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {date}
                    </span>
                  </div>
                  <div className="divide-y divide-border/30">
                    {dateNotifications.map((notification) => (
                      <SwipeToDelete
                        key={notification.id}
                        onDelete={() => deleteNotification(notification.id)}
                        label="Delete notification"
                      >
                      <div
                        onClick={() => handleNotificationClick(notification)}
                        className={`px-5 py-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                          !notification.read ? "bg-blue-50/50 dark:bg-blue-500/5" : ""
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`h-9 w-9 flex items-center justify-center flex-shrink-0 ${
                            !notification.read ? "text-foreground" : "text-muted-foreground"
                          }`}>
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className={`text-sm leading-snug ${
                                !notification.read ? "font-semibold text-foreground" : "font-medium text-foreground/80"
                              }`}>
                                {notification.title}
                              </h3>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs text-muted-foreground">
                                  {formatTimestamp(notification.created_at)}
                                </span>
                                {!notification.read && (
                                  <Lozenge color="#3b82f6" />
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                            {notification.type === "work_request" && !notification.read && (
                              <span className="inline-block mt-1.5 text-xs text-purple-500 font-medium">
                                Tap to respond
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      </SwipeToDelete>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Panel Footer */}
        <div className="flex-shrink-0 border-t border-border/50 bg-background">
          <Link
            href="/dashboard/barber/notifications"
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center gap-2 py-4 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            View all notifications
          </Link>
          <NotificationSheetGrip {...sheet.gripProps} />
        </div>
      </div>

      {/* Work Request Modal */}
      {workRequestNotification && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[60]"
            onClick={() => {
              setWorkRequestNotification(null)
              setWorkRequestShop(null)
            }}
          />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 bg-background border border-border rounded-2xl shadow-2xl z-[70] animate-in zoom-in-95 duration-200 max-h-[80vh] overflow-y-auto max-w-md mx-auto">
            <button
              onClick={() => {
                setWorkRequestNotification(null)
                setWorkRequestShop(null)
              }}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-muted transition-colors z-10"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>

            {workRequestShop?.cover_image && (
              <div className="relative h-32 w-full">
                <img
                  src={workRequestShop.cover_image || "/placeholder.svg"}
                  alt={workRequestShop.name}
                  className="w-full h-full object-cover rounded-t-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-t-2xl" />
              </div>
            )}

            <div
              className={`flex justify-center ${workRequestShop?.cover_image ? "-mt-8" : "pt-6"} pb-3 relative z-10`}
            >
              <div className="h-16 w-16 rounded-full flex items-center justify-center bg-purple-500/10 text-purple-500 border-4 border-background">
                <Briefcase className="h-8 w-8" />
              </div>
            </div>

            <div className="px-6 pb-6 text-center">
              <h3 className="text-xl font-bold text-foreground mb-2">Work Invitation</h3>
              <p className="text-muted-foreground text-sm mb-4">{workRequestNotification.message}</p>

              {workRequestShop && (
                <div className="bg-muted/50 rounded-xl p-4 mb-6 text-left">
                  <h4 className="font-semibold text-foreground mb-2">{workRequestShop.name}</h4>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>
                      {workRequestShop.address}, {formatCity(workRequestShop.city)}, {formatState(workRequestShop.state)}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 bg-transparent"
                  onClick={handleDeclineWorkRequest}
                  disabled={isDeclining || isAccepting}
                >
                  {isDeclining ? "Declining..." : "Decline"}
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleAcceptWorkRequest}
                  disabled={isAccepting || isDeclining}
                >
                  {isAccepting ? "Accepting..." : "Accept"}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Expanded Notification Modal */}
      {expandedNotification && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setExpandedNotification(null)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 bg-background border border-border rounded-2xl shadow-2xl z-[70] animate-in zoom-in-95 duration-200 max-w-md mx-auto p-6 text-center">
            <button
              onClick={() => setExpandedNotification(null)}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>

            <div
              className={`h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                expandedNotification.type === "reservation_approved"
                  ? "bg-green-500/10 text-green-500"
                  : "bg-red-500/10 text-red-500"
              }`}
            >
              {expandedNotification.type === "reservation_approved" ? (
                <CheckCircle className="h-8 w-8" />
              ) : (
                <XCircle className="h-8 w-8" />
              )}
            </div>

            <h3 className="text-xl font-bold text-foreground mb-2">{expandedNotification.title}</h3>
            <p className="text-muted-foreground text-sm mb-6">{expandedNotification.message}</p>

            {expandedNotification.type === "reservation_approved" ? (
              <Button onClick={handleMessageOwner} className="w-full">
                Message Shop Owner
              </Button>
            ) : (
              <Button onClick={handleBrowseShops} className="w-full">
                Browse Other Shops
              </Button>
            )}
          </div>
        </>
      )}

    </>
  )
}
